import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorType } from "@/types";
import { Staff } from "@/types/staff";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { DataTableViewOptions } from "./data-table-view-options";
import {
  useGetStaffsQuery,
  useUpdateStaffMutation,
  useLazyGetAllStaffForExportQuery,
} from "@/services/staffsApi";
import StaffForm from "../modal/form";
import { useGetRolesQuery } from "@/services/rolesApi";
import {
  displayDate,
  displayDateTime,
  displayValue,
  downloadCSV,
  staffMapper,
} from "@/lib/helper";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { UserStatus } from "@/types/enums";
import { userStatus } from "@/lib/constants/global";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  role_id?: string[];
  sort_key: string;
  status?: boolean;
  search_key?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "staff",
};
export default function StaffTableView() {
  const staff = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).staff ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const { data: rolesData } = useGetRolesQuery(orgId);

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState({});

  const {
    data: staffData,
    isLoading,
    refetch,
  } = useGetStaffsQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const [updateStaff] = useUpdateStaffMutation();

  // Fetch business units to resolve ID to name locally (API does not return business_unit_name)
  const { data: businessUnitsData } = useGetBusinessUnitsQuery({ org_id: orgId });
  const businessUnitMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    businessUnitsData?.data?.forEach((bu) => { map[bu.id] = bu.name; });
    return map;
  }, [businessUnitsData]);

  const staffTableData = React.useMemo(() => {
    if (!Array.isArray(staffData?.data)) return [];
    return staffData.data.map((row: Staff) => {
      const ids = row.business_unit_ids ?? [];
      const names = ids.map((id: number) => businessUnitMap[id]).filter(Boolean);
      return {
        ...row,
        business_unit_ids: ids,
        business_unit_names: names,
        business_unit_name: names.length > 0 ? names.join(", ") : '',
      };
    });
  }, [staffData, businessUnitMap]);

  const { toast } = useToast();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getAllStaffForExport] = useLazyGetAllStaffForExportQuery();

  const handleExportSelected = async () => {
    if (selectAllRows) {
      // Export all rows
      try {
        const filterQuery = Object.keys(filterData).length > 0
          ? Object.entries(filterData)
            .filter(([_, value]) => value !== "")
            .map(([key, value]) => `${key}=${value}`)
            .join("&")
          : "";

        const result = await getAllStaffForExport({
          org_id: orgId,
          filterQuery: filterQuery
        }).unwrap();


        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data, "all_staff_list.csv", staffMapper);
          toast({
            variant: "success",
            title: `Exported ${result.data.length} staff successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No staff records found to export.",
          });
        }
      } catch (error) {
        console.error("Export failed:", error);
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Failed to export all staff. Please try again.",
        });
      }
    } else {
      // Export selected rows
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows, "staff_list.csv", staffMapper);
    }
  };

  const handleStatusChange = async (payload: Staff) => {

    try {

      const resp = await updateStaff(payload).unwrap();
      refetch();
      if (resp) {
        toast({
          variant: "success",
          title: "Staff Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail ?? typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const [editStaff, setEditStaff] = useState<Staff | null>(
    null
  );
  const handleOpenForm = (staffData: Staff | null = null) => {
    setEditStaff(staffData);
    setOpen(true);
  };

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  const actionsColumn: ColumnDef<Staff> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={staff}
        handleEdit={handleOpenForm}
        data={row.original}
        refetch={refetch}
      />
    ),
  };

  const columns: ColumnDef<Staff>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "own_staff_id",
      meta: "Staff Id",

      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Staff Id"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.own_staff_id}`.length > 12
                          ? `${row.original.own_staff_id}`.substring(0, 12) +
                          "..."
                          : `${row.original.own_staff_id}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="capitalize text-sm">
                      {displayValue(`${row.original.own_staff_id}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      id: "full_name",
      meta: "Staff Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Staff Name"
          sortKey="first_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              {/* <p className="capitalize">{displayValue(
                row.original.first_name + " " + row.original.last_name
              )}</p> */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.first_name ?? ""} ${row.original.last_name ?? ""}`
                          .length > 8
                          ? `${row.original.first_name ?? ""} ${row.original.last_name ?? ""}`.substring(
                            0,
                            8
                          ) + "..."
                          : `${row.original.first_name ?? ""} ${row.original.last_name ?? ""}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="capitalize">
                      {displayValue(
                        `${row.original.first_name ?? ""} ${row.original.last_name ?? ""}`
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* <p className=" text-xs text-gray-400 ">{row.original.email}</p>   */}
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.email}`,
      id: "email",
      meta: "email",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Email"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="normal-case cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.email}`.length > 15
                          ? `${row.original.email}`.substring(0, 15) + "..."
                          : `${row.original.email}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="normal-case">
                      {displayValue(`${row.original.email}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.mobile_number}`,
      id: "mobile_number",
      meta: "mobile_number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Phone Number"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <p className="capitalize cursor-pointer">
                {/* Display the truncated name */}
                {displayValue(`${row.original.mobile_number}`, true)}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "activated_on",
      meta: "Activation Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Activation Date"
          sortKey="activated_on"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDate(row?.original.activated_on)}
          </div>
        );
      },
    },
    {
      accessorKey: "business_unit_name",
      meta: "Business Unit",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Business Unit"
          sortKey="business_unit_id"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const names = row.original.business_unit_names || [];
        if (names.length === 0) return <span className="text-gray-400">-</span>;

        const visibleNames = names.slice(0, 1);
        const remainingNames = names.slice(1);
        const remaining = remainingNames.length;

        return (
          <div className="flex flex-wrap gap-1 items-center">
            {visibleNames.map((name, index) => (
              <span
                key={index}
                className="inline-block rounded-full bg-green-100 text-green-800 px-3 py-1 text-xs font-medium"
              >
                {name}
              </span>
            ))}
            {remaining > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block rounded-full bg-gray-100 text-gray-600 px-3 py-1 text-xs font-medium cursor-pointer">
                      +{remaining}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <ul className="flex flex-col gap-1 p-1">
                      {remainingNames.map((name, idx) => (
                        <li key={idx} className="whitespace-nowrap font-medium">{name}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "role_name",
      meta: "Role",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Role"
          sortKey="role_id"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex capitalize text-nowrap items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.role_name)}
          </div>
        );
      },
    },

    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value =
          row.original?.status != null ? row.original?.status + "" : "pending";
        const statusLabel = userStatus.filter((r) => r.value === value)[0];

        return (
          <Select
            value={value}
            onValueChange={(e: UserStatus) =>
              handleStatusChange({
                ...row.original,
                status: e
              })
            }
            disabled={value == "pending" || staff == "read"}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Status" className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {userStatus.map(
                (item: any) =>
                  !item.hide && (
                    <SelectItem key={item.value + ""} value={item.value + ""}>
                      {item.label}
                    </SelectItem>
                  )
              )}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "last_checkin",
      meta: "Last Check In",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Check In"
          sortKey="check_in"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex text-nowrap items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDateTime(row?.original.last_checkin)}
          </div>
        );
      },
    },
    {
      accessorKey: "last_online",
      meta: "Last Login",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Login"
          sortKey="last_online"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex text-nowrap items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDateTime(row?.original.last_online)}
          </div>
        );
      },
    },
    ...(staff !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: staffTableData as Staff[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  React.useEffect(() => {
    if (selectAllRows && staffTableData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, staffTableData, table]);

  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "pending", name: "Pending" },
        { id: "inactive", name: "Inactive" },
        { id: "active", name: "Active" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "role_id",
      label: "Role Name",
      options: rolesData?.map((role) => ({ value: role.id, label: role.name })),
      function: (value: string) => handleFilterChange("role_id", value),
    },
  ];

  const totalRecords = staffData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const [open, setOpen] = useState<boolean>(false);
  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3 ">
        {/* Table Filter component */}
        <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={staff !== "read"}
            className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => handleOpenForm()}
          >
            <PlusIcon className="size-4" />
            Create New
          </Button>

          <DataTableViewOptions table={table} action={handleExportSelected} />
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Gmail-style Select All Notification */}
      {(showSelectAllNotification || selectAllRows) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mx-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> staff are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong> staff on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Select all {totalRecords} staff
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-none border border-border ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {/* <Table className="w-full overflow-x-scroll"> */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (staff !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleOpenForm(row.original as Staff)
                      }
                    }}
                    className={staff !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : staffTableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No staffs added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No staffs found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {staffTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
      <StaffForm
        setOpen={setOpen}
        staffData={editStaff}
        setStaffData={setEditStaff}
        open={open}
        refetch={refetch}
      />
    </div>
  );
}